import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../supabaseClient";
import { billingFetch, billingFetchPublic } from "./billingApi";
import TopUpModal from "./TopUpModal";
import "./BillingPage.css";

export default function BillingPage({ userId, isDarkMode, initialToast, onToastShown }) {
  const [balance, setBalance] = useState(null);
  const [usage, setUsage] = useState(0);
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [pendingPlanChange, setPendingPlanChange] = useState(null);
  const [showTopUp, setShowTopUp] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((message, type = "info") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  // Show toast from parent (e.g. Stripe redirect)
  useEffect(() => {
    if (initialToast) {
      showToast(initialToast.text, initialToast.type);
      onToastShown?.();
    }
  }, [initialToast, showToast, onToastShown]);

  const fetchAll = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const [syncData, plansData, invoiceData, subData] = await Promise.allSettled([
        billingFetch("/stripe/sync-usage"),
        billingFetchPublic("/stripe/plans"),
        billingFetch("/stripe/invoices"),
        supabase.from("subscriptions").select("*").eq("user_id", userId).maybeSingle(),
      ]);

      let omBalance = 0;
      let omUsage = 0;
      let sub = null;

      if (syncData.status === "fulfilled") {
        omBalance = syncData.value.openmeter?.balance ?? 0;
        omUsage = syncData.value.openmeter?.usage ?? 0;
      }
      if (plansData.status === "fulfilled") {
        setPlans(Array.isArray(plansData.value) ? plansData.value : []);
      }
      if (invoiceData.status === "fulfilled") {
        setInvoices(invoiceData.value.invoices ?? []);
      }
      if (subData.status === "fulfilled" && subData.value.data) {
        sub = subData.value.data;
        setSubscription(sub);
      }

      // Fallback: if OpenMeter balance is 0 but Supabase shows active subscription, use Supabase data
      if (omBalance <= 0 && sub && (sub.status === "active" || sub.status === "cancelling")) {
        const fallbackBalance = (sub.credits_total ?? 0) - (sub.credits_used ?? 0);
        if (fallbackBalance > 0) {
          omBalance = fallbackBalance;
          omUsage = sub.credits_used ?? 0;
        }
      }

      setBalance(omBalance);
      setUsage(omUsage);
    } catch (err) {
      console.error("Failed to load billing data:", err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchAll();
    const onRefresh = () => fetchAll();
    window.addEventListener("zoomey-credits-refresh", onRefresh);
    return () => window.removeEventListener("zoomey-credits-refresh", onRefresh);
  }, [fetchAll]);

  async function handleSubscribe(priceId) {
    setActionLoading("subscribe");
    try {
      const data = await billingFetch("/stripe/create-checkout", {
        method: "POST",
        body: JSON.stringify({ priceId }),
      });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      console.error("Checkout error:", err);
      showToast("Failed to start checkout. Please try again.", "error");
    } finally {
      setActionLoading(null);
    }
  }

  async function handleChangePlan(priceId) {
    setActionLoading("change");
    try {
      const data = await billingFetch("/stripe/change-plan", {
        method: "POST",
        body: JSON.stringify({ priceId }),
      });
      if (data.success) {
        showToast(`Plan ${data.action} to ${data.plan}! Credits: ${data.credits}/mo`, "success");
        fetchAll();
      }
    } catch (err) {
      console.error("Change plan error:", err);
      showToast("Failed to change plan. Please try again.", "error");
    } finally {
      setActionLoading(null);
    }
  }

  async function handleCancel() {
    setActionLoading("cancel");
    try {
      const data = await billingFetch("/stripe/cancel", { method: "POST" });
      if (data.success) {
        setShowCancelConfirm(false);
        fetchAll();
      }
    } catch (err) {
      console.error("Cancel error:", err);
      showToast("Failed to cancel subscription.", "error");
    } finally {
      setActionLoading(null);
    }
  }

  async function handleResume() {
    setActionLoading("resume");
    try {
      const data = await billingFetch("/stripe/resume", { method: "POST" });
      if (data.success) fetchAll();
    } catch (err) {
      console.error("Resume error:", err);
      showToast("Failed to resume subscription.", "error");
    } finally {
      setActionLoading(null);
    }
  }

  async function handleBillingPortal() {
    setActionLoading("portal");
    try {
      const data = await billingFetch("/stripe/billing-portal", { method: "POST" });
      if (data.url) window.open(data.url, "_blank");
    } catch (err) {
      console.error("Portal error:", err);
      showToast("Failed to open billing portal.", "error");
    } finally {
      setActionLoading(null);
    }
  }

  function getBalanceStatus() {
    if (balance === null) return "loading";
    if (balance <= 0) return "exhausted";
    if (balance < 200) return "low";
    return "healthy";
  }

  const status = getBalanceStatus();
  const currentPlan = subscription?.plan;
  const subStatus = subscription?.status;
  const hasActiveSub = subStatus === "active" || subStatus === "cancelling";
  const currentPlanCredits = plans.find(p => p.name === currentPlan)?.credits
    ?? subscription?.credits_total ?? 0;

  if (!userId) {
    return (
      <div className={`billing-page ${isDarkMode ? "dark" : ""}`}>
        <p>Please login to view billing.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`billing-page ${isDarkMode ? "dark" : ""}`}>
        <h2>Billing &amp; Credits</h2>
        <div className="skeleton-balance">
          <div className="skeleton-line skeleton-sm" />
          <div className="skeleton-line skeleton-lg" />
          <div className="skeleton-line skeleton-sm" />
        </div>
        <div className="skeleton-card">
          <div className="skeleton-line skeleton-md" />
          <div className="skeleton-row">
            <div className="skeleton-line skeleton-sm" />
            <div className="skeleton-line skeleton-sm" />
          </div>
        </div>
        <h3 style={{ marginTop: "1.5rem" }}>Choose a Plan</h3>
        <div className="skeleton-plans">
          <div className="skeleton-plan-card" />
          <div className="skeleton-plan-card" />
        </div>
      </div>
    );
  }

  return (
    <div className={`billing-page ${isDarkMode ? "dark" : ""}`}>
      <h2 className="billing-title">Billing & Credits</h2>

      {/* Balance Card */}
      <div className={`balance-card balance-${status}`}>
        <div className="balance-label">Credit Balance</div>
        <div className="balance-amount">
          {loading ? "..." : Math.round(balance ?? 0)}
        </div>
        <div className="balance-sublabel">credits remaining</div>
        {status === "exhausted" && (
          <div className="balance-warning">
            Credits exhausted. Purchase a plan to continue.
          </div>
        )}
        {status === "low" && (
          <div className="balance-warning warning-low">
            Running low! Consider upgrading your plan.
          </div>
        )}
        {!loading && (
          <div className="balance-usage">
            Used this period: {Math.round(usage)} credits
          </div>
        )}
        {hasActiveSub && (
          <button
            className="plan-buy-btn"
            style={{ marginTop: "1rem", maxWidth: 200 }}
            onClick={() => setShowTopUp(true)}
          >
            + Top Up Credits
          </button>
        )}
      </div>

      {showTopUp && (
        <TopUpModal isDarkMode={isDarkMode} onClose={() => setShowTopUp(false)} />
      )}

      {/* Current Subscription */}
      {subscription && (
        <div className="subscription-card">
          <div className="sub-header">
            <h3 className="sub-title">Current Subscription</h3>
            <span className={`sub-badge sub-badge-${subStatus}`}>
              {subStatus === "active" && "Active"}
              {subStatus === "cancelling" && "Cancelling"}
              {subStatus === "cancelled" && "Cancelled"}
              {subStatus === "past_due" && "Past Due"}
              {subStatus === "expired" && "Expired"}
              {!["active","cancelling","cancelled","past_due","expired"].includes(subStatus) && subStatus}
            </span>
          </div>
          {subscription.pending_plan && (
            <div className="sub-pending-downgrade">
              Downgrades to {subscription.pending_plan.charAt(0).toUpperCase() + subscription.pending_plan.slice(1)} on {subscription.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString() : "next billing cycle"}
            </div>
          )}
          <div className="sub-details">
            <div className="sub-detail">
              <span className="sub-detail-label">Plan</span>
              <span className="sub-detail-value">
                {currentPlan?.charAt(0).toUpperCase() + currentPlan?.slice(1)}
              </span>
            </div>
            <div className="sub-detail">
              <span className="sub-detail-label">Credits</span>
              <span className="sub-detail-value">
                {currentPlanCredits?.toLocaleString()}/mo
              </span>
            </div>
            <div className="sub-detail">
              <span className="sub-detail-label">Used</span>
              <span className="sub-detail-value">
                {Math.round(usage ?? 0).toLocaleString()}
              </span>
            </div>
            {subscription.current_period_end && (
              <div className="sub-detail">
                <span className="sub-detail-label">
                  {subStatus === "active" ? "Renews" :
                   subStatus === "cancelling" ? "Access Until" : "Ended"}
                </span>
                <span className="sub-detail-value">
                  {new Date(subscription.current_period_end).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>
          <div className="sub-actions">
            {subStatus === "active" && (
              <button
                className="sub-cancel-btn"
                onClick={() => setShowCancelConfirm(true)}
                disabled={actionLoading !== null}
              >
                Cancel Subscription
              </button>
            )}
            {subStatus === "cancelling" && (
              <button
                className="sub-resume-btn"
                onClick={handleResume}
                disabled={actionLoading !== null}
              >
                {actionLoading === "resume" ? "Resuming..." : "Resume Subscription"}
              </button>
            )}
            {(subStatus === "cancelled" || subStatus === "expired") && (
              <span className="sub-expired-hint">
                Choose a plan below to resubscribe.
              </span>
            )}
            {hasActiveSub && (
              <button
                className="sub-portal-btn"
                onClick={handleBillingPortal}
                disabled={actionLoading !== null}
              >
                {actionLoading === "portal" ? "Opening..." : "Manage Payment Method"}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Cancel Confirmation */}
      {showCancelConfirm && (
        <div className="cancel-confirm">
          <p>
            Are you sure you want to cancel? You'll keep access until the end of
            your current billing period.
          </p>
          <div className="cancel-confirm-actions">
            <button
              className="cancel-confirm-yes"
              onClick={handleCancel}
              disabled={actionLoading === "cancel"}
            >
              {actionLoading === "cancel" ? "Cancelling..." : "Yes, Cancel"}
            </button>
            <button
              className="cancel-confirm-no"
              onClick={() => setShowCancelConfirm(false)}
            >
              Keep Subscription
            </button>
          </div>
        </div>
      )}

      {/* Plan Change Confirmation */}
      {pendingPlanChange && (() => {
        const isUpgrade = pendingPlanChange.credits > currentPlanCredits;
        const periodEnd = subscription?.current_period_end
          ? new Date(subscription.current_period_end).toLocaleDateString()
          : "the end of your billing period";
        const planDisplayName = pendingPlanChange.name.charAt(0).toUpperCase() + pendingPlanChange.name.slice(1);
        const currentDisplayName = currentPlan?.charAt(0).toUpperCase() + currentPlan?.slice(1);
        return (
          <div className="cancel-confirm">
            <p>
              {isUpgrade
                ? `Upgrade to ${planDisplayName}? Your credits will increase to ${pendingPlanChange.credits?.toLocaleString()}/mo at $${(pendingPlanChange.amount / 100).toFixed(0)}/mo.`
                : `Downgrade to ${planDisplayName}? You'll keep ${currentDisplayName} access until ${periodEnd}. After that, your plan switches to ${planDisplayName} (${pendingPlanChange.credits?.toLocaleString()} credits/mo) at $${(pendingPlanChange.amount / 100).toFixed(0)}/mo.`}
            </p>
            <div className="cancel-confirm-actions">
              <button
                className="cancel-confirm-yes"
                style={{ background: isUpgrade ? "#7c3aed" : "#f59e0b" }}
                onClick={() => {
                  handleChangePlan(pendingPlanChange.priceId);
                  setPendingPlanChange(null);
                }}
                disabled={actionLoading === "change"}
              >
                {actionLoading === "change"
                  ? "Processing..."
                  : isUpgrade
                  ? "Yes, Upgrade"
                  : "Yes, Downgrade"}
              </button>
              <button
                className="cancel-confirm-no"
                onClick={() => setPendingPlanChange(null)}
              >
                Keep Current Plan
              </button>
            </div>
          </div>
        );
      })()}

      {/* Plans */}
      <h3 className="plans-title">
        {hasActiveSub ? "Change Plan" : "Choose a Plan"}
      </h3>
      <div className="plans-grid">
        {plans.map((plan) => {
          const isCurrent = currentPlan === plan.name;
          const priceUsd = (plan.amount / 100).toFixed(0);
          return (
            <div
              key={plan.priceId}
              className={`plan-card ${isCurrent ? "plan-current" : ""}`}
            >
              {isCurrent && <div className="plan-current-badge">Current</div>}
              <div className="plan-name">
                {plan.name.charAt(0).toUpperCase() + plan.name.slice(1)}
              </div>
              <div className="plan-price">${priceUsd}/mo</div>
              <div className="plan-credits">
                {plan.credits.toLocaleString()} credits/month
              </div>
              {isCurrent ? (
                <button className="plan-buy-btn plan-btn-current" disabled>
                  Current Plan
                </button>
              ) : hasActiveSub ? (
                plan.credits > currentPlanCredits ? (
                  <button
                    className="plan-buy-btn"
                    onClick={() => setPendingPlanChange(plan)}
                    disabled={actionLoading !== null}
                  >
                    {actionLoading === "change" ? "Processing..." : "Upgrade"}
                  </button>
                ) : subStatus === "cancelling" ? (
                  <button className="plan-buy-btn plan-btn-current" disabled>
                    Available after cancellation
                  </button>
                ) : subscription?.pending_plan === plan.name ? (
                  <button className="plan-buy-btn plan-btn-current" disabled>
                    Scheduled Downgrade
                  </button>
                ) : subscription?.pending_plan ? (
                  <button className="plan-buy-btn plan-btn-current" disabled>
                    Downgrade pending
                  </button>
                ) : (
                  <button
                    className="plan-buy-btn"
                    onClick={() => setPendingPlanChange(plan)}
                    disabled={actionLoading !== null}
                  >
                    Downgrade
                  </button>
                )
              ) : (
                <button
                  className="plan-buy-btn"
                  onClick={() => handleSubscribe(plan.priceId)}
                  disabled={actionLoading !== null}
                >
                  {actionLoading === "subscribe" ? "Redirecting..." : "Subscribe"}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Invoice History */}
      {invoices.length > 0 && (
        <div className="invoices-section">
          <h3 className="section-title">Invoice History</h3>
          <table className="invoices-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th>Amount</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id}>
                  <td>{new Date(inv.date).toLocaleDateString()}</td>
                  <td>
                    {inv.lines?.[0]?.description || inv.number || "Invoice"}
                  </td>
                  <td>${inv.amount?.toFixed(2)}</td>
                  <td>
                    <span className={`inv-status inv-status-${inv.status}`}>
                      {inv.status}
                    </span>
                  </td>
                  <td>
                    {inv.pdfUrl && (
                      <a
                        href={inv.pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inv-download"
                      >
                        PDF
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* How credits work */}
      <div className="credits-info">
        <h3>How Credits Work</h3>

        <div className="credits-info-section">
          <h4>What are credits?</h4>
          <p>
            Credits are the currency that powers all AI features in Zoomey.
            Every time Zoomey thinks, responds, or performs an action using AI,
            it costs a small number of credits. Your plan includes a monthly
            credit allowance that resets each billing cycle.
          </p>
        </div>

        <div className="credits-info-section">
          <h4>Credits per chat mode</h4>
          <p>
            The mode you select before sending a message determines which AI
            model is used. Smarter models cost more credits because they process
            more deeply:
          </p>
          <table className="credits-table">
            <thead>
              <tr>
                <th>Mode</th>
                <th>~Credits per message</th>
                <th>Best For</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>Simple</td><td>~0.5 - 2</td><td>Quick tasks, daily questions</td></tr>
              <tr><td>Smart</td><td>~2 - 10</td><td>Complex tasks, multi-step actions</td></tr>
              <tr><td>Genius</td><td>~5 - 25</td><td>Deep analysis, research, premium quality</td></tr>
              <tr><td>Voice</td><td>~5 - 30 per minute</td><td>Hands-free voice conversations</td></tr>
            </tbody>
          </table>
        </div>

        <div className="credits-info-section">
          <h4>What uses credits?</h4>
          <p>Credits are consumed by every AI-powered feature, not just chat:</p>
          <ul className="credits-usage-list">
            <li><strong>Chat messages</strong> -- Every message you send in any topic or sidebar chat</li>
            <li><strong>Voice calls</strong> -- Real-time voice conversations (charged per minute of conversation)</li>
            <li><strong>Morning Catch-up</strong> -- Your automated daily brief uses credits to generate a personalized summary</li>
            <li><strong>AI Reminders</strong> -- When a reminder triggers an AI action (e.g. "research and summarize")</li>
            <li><strong>Meeting Summaries</strong> -- Asking questions about your meeting transcripts</li>
            <li><strong>Queue &amp; Scratchpad</strong> -- Popup chats and scratchpad AI sessions</li>
          </ul>
        </div>

        <div className="credits-info-section">
          <h4>What affects credit usage?</h4>
          <ul className="credits-usage-list">
            <li><strong>Message length</strong> -- Longer questions and responses use more credits</li>
            <li><strong>Tool usage</strong> -- When Zoomey uses tools (calendar, tasks, email), each tool call adds a small cost</li>
            <li><strong>Conversation history</strong> -- Longer conversations send more context, using slightly more credits per message</li>
          </ul>
        </div>

        <div className="credits-info-section">
          <h4>Tips to save credits</h4>
          <ul className="credits-usage-list">
            <li>Use <strong>Simple</strong> mode for everyday tasks like adding reminders, checking calendar, or quick questions</li>
            <li>Switch to <strong>Smart</strong> or <strong>Genius</strong> only when you need deeper analysis or research</li>
            <li>Keep questions concise -- shorter prompts use fewer credits</li>
            <li>Start a new topic when switching subjects to reduce conversation history size</li>
          </ul>
        </div>
      </div>

      {/* Toast notification */}
      {toast && (
        <div className={`billing-toast billing-toast-${toast.type}`}>
          <span>{toast.message}</span>
          <button className="billing-toast-close" onClick={() => setToast(null)}>&times;</button>
        </div>
      )}
    </div>
  );
}
